import { useState, useEffect, useMemo } from "react";
import { Check, ChevronDown, Minus, Plus } from "lucide-react";
import { Box, CircularProgress, Divider, Grid, IconButton } from "@mui/material";
import { BLUEBORDER } from "ui-components/CustomButton";
import { Primary } from "ui-components/Buttons";
import { CreateSubscription, ApplyPromoCode, GetSubscriptionDetails, FetchAllPlans, GetSubscription, CheckIfBillingProfileExists, SaveBillingProfile } from "api/subscription";
import { FetchStartupDetailsByID } from "api/profile";
import { useToast } from "context/ToastContext";
import { useSelector } from "react-redux";
import Cookies from "js-cookie";
import { trackEvent } from "utilities/mixpanel";
import { readPendingSubscriptionIntent, clearPendingSubscriptionIntent } from "utilities/subscriptionIntent";
import PaymentSuccessPopup from "./PaymentPopup";
import EnterPromoCodePopup from "./popups/EnterPromoCodePopup";
import UpgradeSubscriptionButton from "./components/UpgradeSubscriptionButton";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@emotion/react";
import BackrrLoadingAnimation from "ui-components/route-components/BackrrLoadingAnimation";
import { capitalizedSentence, getButtonText, isButtonDisabled, navigateToDomain, navigateToMain } from "utilities/index";
import BillingProfilePopup from "./components/BillingDetails";

const getTotalCount = (billingPeriod) => {
    const BILLING_CYCLES = {
        monthly: 360,
        quarterly: 120,
        yearly: 30,
    };
    return BILLING_CYCLES[billingPeriod] || 1;
}

const SubscriptionPurchaseButton = ({
    isPurchasable,
    subscriptionsData,
    plan,
    currentPlanType,
    currentPlanPrice,
    startup_id,
    btnText = "Subscribe",
    isCreatingSubscription = false,
    selectedPlan,
    disabledBtn = false,
    onManageSubscription,
    onUpgradeSuccess,
    handlePlanClick,
    subscriptionPlanType,
    hasActiveSubscription, // NEW: Pass this down to avoid recalculation
    offerId, // An offer code validated on this page, carried into the plan change
    autoTrigger = false, // Plan picked through the URL: run the plan-change flow without a click
    onAutoTriggered
}) => {
    // Helper: Is current plan the same as shown plan?
    const isCurrentPlan =
        subscriptionsData &&
        subscriptionsData.current_end &&
        subscriptionsData.plan_id === plan?.plan_id;

    // Helper: Is this a free plan (price 0)?
    const isFreePlan = plan?.price === 0;

    // 1. If not purchasable: render nothing or disabled (optional)
    if (!isPurchasable) return null;

    // 2. If user HAS an active subscription
    if (hasActiveSubscription) {
        if (isCurrentPlan) {
            // a. CURRENT PLAN (same)
            return (
                <Primary
                    className="btn w-100 p-2"
                    style={{ opacity: 0.7 }}
                    sx={{
                        height: { xs: "44px", sm: "50px" },
                    }}
                    onClick={onManageSubscription}
                >
                    Current Plan
                </Primary>
            );
        } else {
            // b. NOT current plan, show upgrade/downgrade button
            if (isFreePlan) {
                return (
                    <Primary
                        className="btn w-100 p-2"
                        sx={{
                            height: { xs: "44px", sm: "50px" },
                        }}
                        disabled={true}
                    >
                        {btnText}
                    </Primary>
                );
            }

            if (subscriptionsData?.subscription_id?.includes("admin-")) {
                return (
                    <Primary
                        className={`btn w-100 p-2`}
                        sx={{
                            height: { xs: "44px", sm: "50px" },
                        }}
                        onClick={() => {
                            if (!disabledBtn) handlePlanClick(plan);
                        }}
                        disabled={disabledBtn}
                    >
                        {isCreatingSubscription && selectedPlan?.planType === plan.planType ? (
                            <CircularProgress sx={{ height: '25px !important', width: '25px !important', color: 'white' }} />
                        ) : (
                            btnText
                        )}
                    </Primary>
                );
            }

            return (
                <UpgradeSubscriptionButton
                    subscriptionId={subscriptionsData?.subscription_id}
                    newPlanId={plan?.plan_id}
                    currentPlanType={currentPlanType}
                    newPlanType={plan?.planType}
                    newPlanPrice={plan?.price}
                    currentPlanPrice={currentPlanPrice || 0}
                    buttonBG={plan?.buttonBG}
                    planTitle={plan?.title}
                    onUpgradeSuccess={onUpgradeSuccess}
                    isFreePlan={isFreePlan}
                    currentPlanId={subscriptionsData?.plan_id}
                    startupId={startup_id}
                    currentPlanEndDate={subscriptionsData?.formatted_current_end}
                    paymentMethod={subscriptionsData?.payment_method}
                    newPlanBillingPeriod={plan?.period}
                    subscriptionPlanType={subscriptionPlanType}
                    offerId={offerId}
                    autoTrigger={autoTrigger}
                    onAutoTriggered={onAutoTriggered}
                />
            );
        }
    }

    // 3. No active subscription - FREE PLAN shows as "Current Plan" by default
    if (isFreePlan) {
        return (
            <Primary
                className="btn w-100 p-2"
                style={{ opacity: 0.7 }}
                disabled={true}
                sx={{
                    height: { xs: "44px", sm: "50px" },
                }}
            >
                Current Plan
            </Primary>
        );
    }

    // 4. No active subscription - PAID PLANS show action button
    return (
        <Primary
            className={`btn w-100 p-2`}
            sx={{
                height: { xs: "44px", sm: "50px" },
            }}
            onClick={() => {
                if (!disabledBtn) handlePlanClick(plan);
            }}
            disabled={disabledBtn}
        >
            {isCreatingSubscription && selectedPlan?.planType === plan.planType ? (
                <CircularProgress sx={{ height: '25px !important', width: '25px !important', color: 'white' }} />
            ) : (
                btnText
            )}
        </Primary>
    );
};

const PricingCards = ({ allPlans = {}, isLoadingPlans = false, handleClick, isPurchasable = false }) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const { addToast } = useToast();
    const startupDetails = useSelector((state) => state?.startupDetails?.data);
    const startup_id = Cookies.get("X-ST-ID") || null;

    const [billingPeriod, setBillingPeriod] = useState("monthly");
    const [expandedPlans, setExpandedPlans] = useState({});
    const [isMobile, setIsMobile] = useState(false);

    // Payment & Subscription States
    const [showPaymentStatus, setShowPaymentStatus] = useState(false);
    const [isPaymentSuccess, setIsPaymentSuccess] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [openPromoCodePopup, setOpenPromoCodePopup] = useState(false);
    const [offerId, setOfferId] = useState(null);
    // planType of the card whose plan-change flow should open on its own (URL intent, existing subscriber).
    const [autoChangePlanType, setAutoChangePlanType] = useState(null);
    const { is_subscribed, current_plan } = useSelector((state) => state?.startupDetails);

    // API Hooks
    const { data: userData, refetch: refetchStartupDetails } = FetchStartupDetailsByID({ startup_id });
    const { data: subscriptionsData, refetch: refetchSubscriptionData, isLoading: isLoadingSubscriptionsData } = GetSubscriptionDetails({ id: startup_id });
    const { mutateAsync: getSubscriptionStatusAsync, isLoading: isLoadingSubscriptionStatus } = GetSubscription({ id: startup_id });
    const { mutateAsync: createSubscriptionAsync, isLoading: isCreatingSubscription } = CreateSubscription();
    const { mutateAsync: verifyPromoCode, isLoading: isSubmittingPromoCode } = ApplyPromoCode();
    const { mutateAsync: checkBillingProfileExists, isLoading: isCheckingBillingProfileExists } = CheckIfBillingProfileExists();
    const { mutateAsync: saveBillingProfile, isLoading: isSavingBillingProfile } = SaveBillingProfile();

    // OPTIMIZATION: Calculate if there's an active subscription ONCE
    const hasActiveSubscription = useMemo(() => {
        const now = Date.now();
        return !!(subscriptionsData &&
            subscriptionsData?.current_end &&
            now / 1000 < subscriptionsData.current_end);
    }, [subscriptionsData]);

    // Load Razorpay Script
    useEffect(() => {
        const script = document.createElement("script");
        script.src = "https://checkout.razorpay.com/v1/checkout.js";
        script.async = true;
        document.body.appendChild(script);

        script.onload = () => {
            console.log("Razorpay script loaded");
        };
        return () => {
            if (document.body.contains(script)) {
                document.body.removeChild(script);
            }
        };
    }, []);

    // Plans that are still on sale. Retired plans stay in the config so existing
    // subscriptions keep billing against their razorpay plan ids, but they are not
    // offered to anyone new: the backend marks those with visible === false.
    const offeredPlans = useMemo(() => {
        if (!allPlans?.plans || !Array.isArray(allPlans.plans)) return null;
        // `visible` already reflects SHOW_LEGACY_PLANS: the backend decides which
        // generation of plans is on sale, and the two are never mixed.
        return allPlans.plans.filter(plan => plan.enabled && plan.visible !== false);
    }, [allPlans]);

    // A billing period is only offered if some plan on sale actually sells it. The
    // yearly-only plans leave this as ["yearly"], which hides the period toggle.
    const availablePeriods = useMemo(() => {
        if (!offeredPlans?.length) return [];
        return ["monthly", "quarterly", "yearly"].filter(period =>
            offeredPlans.some(plan => plan.razorpay_config?.[period]?.plan_id)
        );
    }, [offeredPlans]);

    // Resolved during render, not in an effect: the selected period starts at
    // "monthly", and an effect correcting it only runs after a first render that
    // would already have tried to price a plan that has no monthly price.
    const activePeriod = useMemo(() => {
        if (!availablePeriods.length) return billingPeriod;
        return availablePeriods.includes(billingPeriod) ? billingPeriod : availablePeriods[0];
    }, [availablePeriods, billingPeriod]);

    // Keeps the toggle's highlight in step with what is actually being shown.
    useEffect(() => {
        if (activePeriod !== billingPeriod) {
            setBillingPeriod(activePeriod);
        }
    }, [activePeriod, billingPeriod]);

    // Transform API data to component-friendly format
    const plans = useMemo(() => {
        if (!offeredPlans) return null;

        return offeredPlans
            // A plan only appears under a period it actually sells. Without this,
            // a yearly-only plan viewed on the monthly tab has no price and
            // renders as "Free". The free tier sells no period at all — it has no
            // razorpay ids anywhere — which is what keeps it on the page rather
            // than a price of zero, a value an editor can clear.
            .filter(plan =>
                plan.razorpay_config?.[activePeriod]?.plan_id ||
                !Object.values(plan.razorpay_config || {}).some(config => config?.plan_id)
            )
            .map(plan => ({
                name: plan.title || plan.plan_type.charAt(0).toUpperCase() + plan.plan_type.slice(1),
                title: plan.title || plan.plan_type.charAt(0).toUpperCase() + plan.plan_type.slice(1),
                subtitle: plan.subtitle,
                price: plan.pricing?.[activePeriod] ?? 0,
                plan_id: plan.razorpay_config?.[activePeriod]?.plan_id,
                period: (plan.pricing?.[activePeriod] ?? 0) === 0 ? "" : `${activePeriod}`,
                buttonText: plan.buttonText,
                buttonClass: plan.buttonClass,
                buttonBG: plan.buttonClass === "btn-gradient"
                    ? `linear-gradient(135deg,#7852f8,#84aef6)`
                    : "#0046ff",
                borderColor: "#0046ff",
                popular: plan.popular,
                symbol: plan.pricing.symbol,
                currency: plan.pricing.currency,
                // A current-generation (yearly-only) plan is sold on what it works out
                // to per month. Legacy plans keep their original full-period price.
                // Config can override the figure when the CEO wants a specific rounding.
                monthlyEquivalent:
                    plan.current_generation && activePeriod === "yearly" && plan.pricing?.yearly > 0
                        ? (plan.pricing.monthly_equivalent ?? Math.round(plan.pricing.yearly / 12))
                        : null,
                features: plan.features || [],
                planType: plan.plan_type,
                enabled: plan.enabled,
                razorpay_config: plan.razorpay_config || {},
                limits: plan.limits || {}
            }));
    }, [offeredPlans, activePeriod]);

    // Consume pending subscription intent: auto-select and trigger the matched plan.
    useEffect(() => {
        // Wait for the subscription too: without it an existing subscriber looks new
        // and would be sent to a fresh checkout instead of a plan change.
        if (!plans?.length || isLoadingSubscriptionsData) return;
        const intent = readPendingSubscriptionIntent();
        if (!intent) return;

        const matchedPlan = plans.find(p => p.planType === intent.planType);
        if (!matchedPlan) {
            clearPendingSubscriptionIntent();
            return;
        }

        // If billing period differs, update it and let plans recompute with the right period's plan_id first.
        // A period that is no longer sold is ignored, otherwise this and the effect
        // that snaps billingPeriod back to an available period fight each other.
        if (
            intent.billingPeriod &&
            intent.billingPeriod !== billingPeriod &&
            availablePeriods.includes(intent.billingPeriod)
        ) {
            setBillingPeriod(intent.billingPeriod);
            return; // let `plans` recompute with the right period's plan_id first
        }

        // One-shot: clear before firing so no re-trigger on re-render.
        clearPendingSubscriptionIntent();

        // From here, do what clicking this plan's button would do (see SubscriptionPurchaseButton).
        if (!hasActiveSubscription) {
            handlePlanClick(matchedPlan);
            return;
        }

        const matchedPlanId = matchedPlan.razorpay_config?.[activePeriod]?.plan_id;
        if (subscriptionsData?.plan_id === matchedPlanId) {
            addToast(`You're already on the ${matchedPlan.title} plan (${capitalizedSentence(activePeriod)}).`, "info");
            return;
        }

        // Free plan: its button is disabled for subscribers.
        if (matchedPlan.price === 0) return;

        // Admin-granted subscriptions buy through checkout, like the button does.
        if (subscriptionsData?.subscription_id?.includes("admin-")) {
            handlePlanClick(matchedPlan);
            return;
        }

        // Upgrade (proration), downgrade (scheduled) or contact-us: decided by UpgradeSubscriptionButton.
        setAutoChangePlanType(matchedPlan.planType);
    }, [plans, billingPeriod, isLoadingSubscriptionsData]);

    // Detect mobile screen size
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window?.innerWidth < 576);
        };

        handleResize();
        window?.addEventListener("resize", handleResize);

        return () => window?.removeEventListener("resize", handleResize);
    }, []);

    // Toggle features visibility
    const togglePlanFeatures = (planKey) => {
        setExpandedPlans(prev => ({
            ...prev,
            [planKey]: !prev[planKey]
        }));
    };

    // Calculate savings percentages
    const savingsPercentages = useMemo(() => {
        if (!offeredPlans?.length) return { quarterly: 0, yearly: 0 };

        // Compare against a plan that is actually on sale and priced monthly —
        // a retired plan's monthly price would advertise a saving nobody can get.
        const referencePlan = offeredPlans.find(p => p.pricing?.monthly > 0);
        if (!referencePlan) return { quarterly: 0, yearly: 0 };

        const monthly = referencePlan.pricing.monthly || 0;
        const quarterly = referencePlan.pricing.quarterly || 0;
        const yearly = referencePlan.pricing.yearly || 0;

        const quarterlyTotal = monthly * 3;
        const yearlyTotal = monthly * 12;

        return {
            quarterly: quarterlyTotal > 0 ? Math.round(((quarterlyTotal - quarterly) / quarterlyTotal) * 100) : 0,
            yearly: yearlyTotal > 0 ? Math.round(((yearlyTotal - yearly) / yearlyTotal) * 100) : 0
        };
    }, [offeredPlans]);

    // Payment Verification Polling
    const handleFetchPaymentDetails = (plan_id, subscription_id) => {
        let attempts = 0;
        const maxAttempts = 200;

        const refetchData = () => {
            if (attempts >= maxAttempts) {
                console.error("Max attempts reached, stopping retries.");
                setIsVerifying(false);
                return;
            }

            attempts++;

            getSubscriptionStatusAsync({
                subscription_id: subscription_id,
                startupId: startup_id,
                plan_id: plan_id
            }).then((data) => {
                if (data?.data?.data?.charge_at) {
                    refetchSubscriptionData();
                    setIsVerifying(false);
                    setIsPaymentSuccess(true);
                    trackEvent("Upgrade Plan - Razorpay Payment Success", {
                        startup_name: startupDetails?.startup_name,
                    });
                } else {
                    setTimeout(refetchData, 5000);
                }
            }).catch(() => {
                setTimeout(refetchData, 5000);
            });
        };

        refetchData();
    };

    // Main Payment Handler
    const handlePayment = async ({ plan_id, offer_id, config = {}, planForPayment }) => {
        const response = await checkBillingProfileExists();

        // console.log("Billing profile exists response:", response);
        if (response?.data?.exists === false) {
            setOpenBillingProfilePopup(true);
            if (offer_id) {
                setOfferId(offer_id);
            }
        }
        else {
            await handleSubscription({ plan_id, offer_id, config, planForPayment });
        }
    };

    const handleSubscription = async ({ plan_id, offer_id, config = {}, planForPayment }) => {
        // Handle subscription logic here
        trackEvent("Upgrade Plan - Razorpay Payment Initiated", {
            startup_name: startupDetails?.startup_name,
            selected_plan: {
                title: planForPayment?.name,
                period: planForPayment?.period,
                planType: planForPayment?.planType,
                plan_id: planForPayment?.plan_id,
            }
        });

        setOpenPromoCodePopup(false);
        setOpenBillingProfilePopup(false);

        const checkBillingProfileExistsResponse = await checkBillingProfileExists();

        let payload = {
            plan_id: plan_id,
            customer_notify: 1,
            quantity: 1,
            total_count: getTotalCount(activePeriod),
            // total_count: 100,
            startupId: startup_id,
            ...(offer_id ? { offer_id: offer_id } : {})
        };

        await createSubscriptionAsync(payload, {
            onSuccess: (response) => {
                const data = response.data;

                const options = {
                    key: process.env.REACT_APP_RZ_CLIENT_ID,
                    subscription_id: data.subscription_id,
                    // name: "Backrr",
                    description: `Subscribe to ${selectedPlan?.name} (${capitalizedSentence(selectedPlan?.period)}) Plan`,
                    // image: "https://backrr.com/assets/public_home_logo_transparent.svg",
                    handler: function (response) {
                        setIsVerifying(true);
                        setShowPaymentStatus(true);
                        handleFetchPaymentDetails(plan_id, data.subscription_id);
                    },
                    prefill: {
                        name: checkBillingProfileExistsResponse?.data?.billing_details?.contact_name || startupDetails?.startup_name,
                        email: checkBillingProfileExistsResponse?.data?.billing_details?.contact_email || startupDetails?.email,
                        contact: (checkBillingProfileExistsResponse?.data?.billing_details?.phone ? checkBillingProfileExistsResponse?.data?.billing_details?.phone_code + checkBillingProfileExistsResponse?.data?.billing_details?.phone : startupDetails?.phone_code + startupDetails?.mobile_number) ,
                    },
                    theme: {
                        // color: "#3399cc",
                    },
                };

                const rzp = new window.Razorpay(options);
                rzp.on('payment.failed', function (response) {
                    addToast("Payment Failed!", "error");
                });
                rzp.on('payment.cancelled', function (response) {
                    // Payment cancelled
                });

                rzp.open();
            },
            onError: (err) => {
                addToast(err?.response?.data?.message, "error");
            }
        });
    }

    // Promo Code Handlers
    const handleClosePromoCodePopup = () => {
        setOpenPromoCodePopup(false);
    };

    const handleOpenPromoCodePopup = (plan) => {
        setSelectedPlan(plan);
        setOpenPromoCodePopup(true);
    };

    const handleSubmitPromoCode = (props) => {
        verifyPromoCode({
            offer_code: props?.offer_code,
            payload: {
                plan_id: selectedPlan?.plan_id,
                startup_id: startup_id
            }
        }, {
            onSuccess: async (response) => {
                if (response?.data?.statusCode === 200) {
                    if (response?.data?.offer?.offer_id) {
                        // An existing subscriber is changing plan, not checking out:
                        // hold the offer so the upgrade call can carry it, instead of
                        // opening a second subscription alongside the one they have.
                        if (hasActiveSubscription) {
                            setOfferId(response?.data?.offer?.offer_id);
                            handleClosePromoCodePopup();
                            addToast(
                                `${response?.data?.message || "Offer applied"} — it will be applied when you confirm the plan change.`,
                                "success"
                            );
                            return;
                        }

                        await handleSubscription({
                            plan_id: selectedPlan?.plan_id,
                            offer_id: response?.data?.offer?.offer_id,
                            planForPayment: selectedPlan,
                            config: selectedPlan?.razorpay_config
                        });
                    }
                } else {
                    addToast(response?.data?.message, "error");
                }
            },
            onError: ({ response }) => {
                addToast(response?.data?.message, "error");
            }
        });
    };

    // Plan Click Handler
    const handlePlanClick = (plan) => {
        if (handleClick) {
            handleClick()
        }
        else {
            if (plan.price === 0) {
                // Free plan - do nothing or show message
                return;
            }

            // Get the plan_id based on the period actually being shown
            const razorpay_config = plan.razorpay_config?.[activePeriod];
            const plan_id = razorpay_config?.plan_id;

            if (!plan_id) {
                addToast("Plan configuration not available", "error");
                return;
            }

            // Prepare plan object for payment
            const planForPayment = {
                ...plan,
                plan_id: plan_id
            };

            setSelectedPlan(planForPayment);

            // Directly initiate payment
            handlePayment({ plan_id: plan_id, config: razorpay_config, planForPayment });
        }
    };

    const [openBillingProfilePopup, setOpenBillingProfilePopup] = useState(false);

    // OPTIMIZATION: Only calculate plan comparison details if there's an active subscription
    const currentPlanType = useMemo(() => {
        if (!hasActiveSubscription || !subscriptionsData?.plan_id || !allPlans?.plans) return "";

        // Matched across every frequency, not just the one being viewed: a legacy
        // monthly subscriber must still be recognised while the page shows yearly.
        const currentPlan = allPlans?.plans?.find(p =>
            Object.values(p?.razorpay_config || {}).some(
                cfg => cfg?.plan_id === subscriptionsData.plan_id
            )
        );
        return currentPlan?.plan_type || "";
    }, [hasActiveSubscription, subscriptionsData, allPlans]);

    const currentPlan = useMemo(() => {
        if (!hasActiveSubscription || !subscriptionsData?.plan_id || !allPlans?.plans) return null;

        return allPlans?.plans?.find(plan => {
            const configs = plan?.razorpay_config || {};
            return Object.values(configs).some(cfg => cfg?.plan_id === subscriptionsData?.plan_id);
        });
    }, [hasActiveSubscription, subscriptionsData, allPlans]);

    const currentPlanPrice = useMemo(() => {
        if (!hasActiveSubscription || !currentPlan) return 0;
        return currentPlan?.pricing?.[billingPeriod] || 0;
    }, [hasActiveSubscription, currentPlan, billingPeriod]);

    const currentBillingPeriod = useMemo(() => {
        if (!hasActiveSubscription || !subscriptionsData?.plan_id || !currentPlan) return billingPeriod;

        try {
            const configs = currentPlan?.razorpay_config || {};
            const matched = Object.keys(configs).find(k => configs[k]?.plan_id === subscriptionsData?.plan_id);
            return matched || billingPeriod;
        } catch (e) {
            return billingPeriod;
        }
    }, [hasActiveSubscription, subscriptionsData, currentPlan, billingPeriod]);

    const handleSubmitBillingProfile = async (values) => {
        await saveBillingProfile({
            startup_id: startup_id,
            billing_details: values
        }, {
            onSuccess: async (response) => {
                setOpenBillingProfilePopup(false);
                addToast("Billing details saved", "success");
                await handleSubscription({ plan_id: selectedPlan?.plan_id, offer_id: offerId, config: selectedPlan?.razorpay_config, planForPayment: selectedPlan });
                // addToast("Billing profile created successfully. Please proceed with the subscription again.", "success");
            },
            onError: ({ response }) => {
                addToast(response?.data?.message || "Failed to save billing details.", "error");
            }
        });
    };

    // Loading state
    if (isLoadingPlans || isLoadingSubscriptionsData) {
        return (
            <BackrrLoadingAnimation />
        );
    }

    // Error state
    if (!plans || plans.length === 0) {
        return (
            <section className="py-5 xs-px-1 px-sm-2 px-1" id="pricing">
                <div className="container-xxl text-center">
                    <p className="description text-muted">No pricing plans available at the moment.</p>
                </div>
            </section>
        );
    }

    return (
        <>
            <section className="py-5 xs-px-1 px-sm-2 px-1" id="pricing">
                <style>
                    {`.btn:active, .btn {
                        border: 0px solid transparent;
                    }`}
                </style>
                <div className="container text-center mb-5 h-auto overflow-visible justify-content-center">
                    {/* <Box component={'p'} className="description text-muted text-uppercase fw-regular mb-5" sx={{
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        opacity: 0,
                        display: { xs: 'none', md: 'block' }
                    }}>
                        CHOOSE YOUR BILLING PERIOD
                    </Box> */}
                    {/* Not rendered at all when every plan on sale bills yearly.
                        Hiding it with a style does not work here: the bootstrap
                        display utility on this Box is !important and wins. */}
                    {availablePeriods.length > 1 && (
                    <Box className="d-inline-flex flex-col align-items-center gap-2 bg-light" sx={{
                        border: `1px solid #0046ff`,
                        background: "white",
                        padding: { xs: "10px !important", md: "10px !important" },
                        borderRadius: "12px",
                        marginTop: { xs: "1rem !important", md: '0px !important' }
                    }}>
                        <button
                            onClick={() => setBillingPeriod("monthly")}
                            className={`btn px-2 py-2 rounded fw-bold color-black border-none ${billingPeriod === "monthly" ? "" : "text-decoration-none"}`}
                            style={{
                                background: billingPeriod === "monthly" ? "linear-gradient(90deg, #d6efff, #f0dcff)" : ""
                            }}
                        >
                            <span className="description fw-bold">Monthly</span>
                        </button>
                        <button
                            onClick={() => setBillingPeriod("quarterly")}
                            className={`btn px-2 py-2 rounded fw-bold position-relative color-black border-none ${billingPeriod === "quarterly" ? "" : "text-decoration-none"}`}
                            style={{
                                background: billingPeriod === "quarterly" ? "linear-gradient(90deg, #d6efff, #f0dcff)" : ""
                            }}
                        >
                            <span className="description fw-bold">Quarterly</span>
                            {savingsPercentages.quarterly > 0 && (
                                <span className="position-absolute start-50 translate-middle badge rounded-pill py-2 description"
                                    style={{
                                        backgroundColor: theme?.palette?.customColors?.y25,
                                        color: "#0046ff",
                                        top: "-1rem",
                                        fontSize: "0.7rem"
                                    }}>
                                    Save {savingsPercentages.quarterly}%
                                </span>
                            )}
                        </button>
                        <button
                            onClick={() => setBillingPeriod("yearly")}
                            className={`btn px-2 py-2 rounded fw-bold position-relative color-black border-none ${billingPeriod === "yearly" ? "" : "text-decoration-none"}`}
                            style={{
                                background: billingPeriod === "yearly" ? "linear-gradient(90deg, #d6efff, #f0dcff)" : ""
                            }}
                        >
                            <span className="description fw-bold">Yearly</span>
                            {savingsPercentages.yearly > 0 && (
                                <span className="position-absolute start-50 translate-middle badge rounded-pill py-2 description"
                                    style={{
                                        backgroundColor: theme?.palette?.customColors?.y25,
                                        color: "#0046ff",
                                        top: "-1rem",
                                        fontSize: "0.7rem"
                                    }}>
                                    Save {savingsPercentages.yearly}%
                                </span>
                            )}
                        </button>
                    </Box>
                    )}
                </div>

                {/* A subscriber on a retired plan no longer sees it as a card, so say
                    plainly that it keeps running rather than leaving them to guess. */}
                {hasActiveSubscription && currentPlan && currentPlan.visible === false ? (
                    <Box className="mx-auto mb-4 px-3 py-2 description__3" sx={{
                        maxWidth: "720px",
                        borderRadius: "12px",
                        border: "1px solid #7852f8",
                        background: "#EFEAF6",
                        color: "#212529",
                        textAlign: "center"
                    }}>
                        {`You're on ${currentPlan?.title || "your current plan"}${currentBillingPeriod ? ` (${currentBillingPeriod})` : ""}. It keeps renewing as usual — moving to a plan below is optional, and you'll be credited for the time you've already paid for.`}
                    </Box>
                ) : null}

                {/* Pricing Cards */}
                <Box className="plans-pricing-cards row row-cols-1 row-cols-sm-3 position-relative mx-auto" sx={{
                    justifyContent: "center",
                    gap: { xs: "0px !important", sm: "0px !important", md: "0px !important", lg: "30px !important" },
                    flexWrap: { xs: "", sm: "nowrap !important" }
                }}>
                    {plans.map((plan, index) => {
                        const planKey = `${plan?.planType}-${billingPeriod}`;
                        const isExpanded = expandedPlans[planKey];
                        const isFree = plan.price === 0;

                        // OPTIMIZATION: Only calculate button text and disabled state if there's an active subscription
                        const btnText = hasActiveSubscription ? getButtonText(
                            plan?.title,
                            plan?.planType,
                            currentPlanType,
                            plan?.price === 0,
                            plan?.price,
                            currentPlanPrice,
                            billingPeriod,
                            currentBillingPeriod
                        ) : "Subscribe";

                        const disabledBtn = hasActiveSubscription ? (
                            isButtonDisabled(
                                plan?.price,
                                currentPlanPrice,
                                plan?.planType,
                                currentPlanType,
                                billingPeriod,
                                currentBillingPeriod
                            ) || isCreatingSubscription
                        ) : (isCreatingSubscription || (isPurchasable && plan?.price === 0));

                        return (
                            <Box key={planKey} className="col flex-1" sx={{
                                width: "100%",
                                maxWidth: { xs: "360px !important", sm: "33% !important", md: "33% !important", lg: "360px !important" },
                                minHeight: { xs: "auto", sm: "auto", md: "auto" },
                                padding: { xs: "0.5rem !important", sm: "0.2rem !important", md: "0.2rem !important" },
                                marginBottom: { xs: "0.5rem", sm: undefined }
                            }}>
                                <div
                                    className={`card h-100 position-relative ${plan.popular ? "gradient-border-card" : "border-1"}`}
                                    style={{
                                        borderRadius: "18px",
                                        paddingInline: "0px",
                                        paddingBlock: "1.5rem",
                                        borderColor: plan.popular ? "transparent" : plan.borderColor,
                                        background: plan.popular
                                            ? `linear-gradient(white, white) padding-box, linear-gradient(135deg, #0046ff 0%, #CC5ADF 100%) border-box`
                                            : "white",
                                        border: plan.popular ? "2px solid transparent" : `1px solid ${plan.borderColor}`
                                    }}
                                >
                                    {plan.popular && (
                                        <div className="position-absolute top-0 start-50 translate-middle">
                                            <span className="badge text-white px-3 py-2" style={{
                                                background: "linear-gradient(90deg, #7852f8, #84aef6)",
                                                borderRadius: "13px"
                                            }}>
                                                Most Popular
                                            </span>
                                        </div>
                                    )}

                                    <Box className="card-body" sx={{
                                        padding: { xs: "0rem 1.2rem !important", sm: "1rem", md: "1rem" }
                                    }}>
                                        <div className="mb-4">
                                            <h5 className="heading__5 fw-bold mb-0 font-DMSans" style={{ color: '#212529' }}>{plan.name}</h5>
                                            <p className="description__3" style={{ color: '#212529' }}>{plan.subtitle}</p>
                                        </div>

                                        {/* Price reads as a monthly cost with the real charge stated
                                            under it: the figure people compare on is per month, but
                                            what leaves their account is the yearly amount. */}
                                        <div className="mb-2">
                                            {plan.price === 0 ? (
                                                <span className="display-5 fw-bold" style={{ color: '#212529', fontFamily: '"Lato", sans-serif' }}>Free</span>
                                            ) : (
                                                <>
                                                    <div className="d-flex align-items-baseline flex-wrap" style={{ columnGap: "0.4rem" }}>
                                                        <div className="d-flex align-items-baseline">
                                                            <span className="fs-4" style={{ color: '#212529', fontFamily: '"Lato", sans-serif' }}>{plan.symbol || "₹"}</span>
                                                            <span className="display-5 fw-bold" style={{ color: '#212529', fontFamily: '"Lato", sans-serif' }}>
                                                                {(plan.monthlyEquivalent ?? plan.price).toLocaleString('en-IN')}
                                                            </span>
                                                        </div>
                                                        <span className="text-nowrap" style={{ color: '#212529', fontFamily: '"Lato", sans-serif', fontSize: "0.85rem" }}>
                                                            {`${plan.currency || ""} / ${plan.monthlyEquivalent ? "month" : String(plan.period)?.slice(0, -2)}`}
                                                        </span>
                                                    </div>
                                                    {/* On its own line under the amount rather than in a column beside
                                                        it: beside the amount a narrow card squeezes the tax note into a
                                                        strip of one or two words per line. */}
                                                    {/* Legacy plans (SHOW_LEGACY_PLANS) are priced for the period itself, so the billing line would only repeat it. */}
                                                    {!allPlans?.show_legacy_plans && (
                                                        <div className="text-muted" style={{ fontFamily: '"Lato", sans-serif', fontSize: "0.75rem", lineHeight: 1.35 }}>
                                                            {/* Whether tax is already in the price is a fact about the
                                                                region, recorded once in the config as a tax rate. */}
                                                            {`${plan.monthlyEquivalent ? "billed yearly" : `billed ${plan.period}`}${allPlans?.tax_note ? ` (${allPlans.tax_note})` : ""}`}
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>

                                        <Box className="mb-4">
                                            <SubscriptionPurchaseButton
                                                currentPlanPrice={currentPlanPrice}
                                                currentPlanType={currentPlanType}
                                                handlePlanClick={handlePlanClick}
                                                isCreatingSubscription={isCreatingSubscription}
                                                plan={plan}
                                                selectedPlan={selectedPlan}
                                                disabledBtn={disabledBtn}
                                                btnText={btnText}
                                                subscriptionsData={subscriptionsData}
                                                isPurchasable={isPurchasable}
                                                onUpgradeSuccess={() => {
                                                    refetchSubscriptionData();
                                                }}
                                                startup_id={startup_id}
                                                onManageSubscription={() => navigate("/manage_subscription")}
                                                subscriptionPlanType={subscriptionsData?.plan_type}
                                                hasActiveSubscription={hasActiveSubscription}
                                                offerId={offerId}
                                                autoTrigger={autoChangePlanType === plan.planType}
                                                onAutoTriggered={() => setAutoChangePlanType(null)}
                                            />
                                            {/* Promo Code Link — also offered to existing
                                                subscribers, since changing plan is where a
                                                migration offer is most likely to be used. */}
                                            {(
                                                <Box display={"flex"} justifyContent={"flex-end"} sx={{
                                                    visibility: !isFree && isPurchasable && !(subscriptionsData && subscriptionsData?.plan_id === plan?.plan_id) ? "visible" : "hidden"
                                                }}>
                                                    <BLUEBORDER
                                                        component="label"
                                                        role={undefined}
                                                        tabIndex={-1}
                                                        sx={{
                                                            backgroundColor: 'transparent !important',
                                                            border: "0",
                                                            padding: "0",
                                                            marginTop: "0.5rem",
                                                            marginLeft: "auto",
                                                            color: "#0046ff",
                                                            textTransform: 'none',
                                                            cursor: 'pointer',

                                                        }}
                                                        onClick={() => {
                                                            const razorpay_config = plan.razorpay_config?.[activePeriod];
                                                            const plan_id = razorpay_config?.plan_id;
                                                            handleOpenPromoCodePopup({ ...plan, plan_id });
                                                        }}
                                                    >
                                                        <div>Have an Offer Code?</div>
                                                    </BLUEBORDER>
                                                </Box>
                                            )}
                                        </Box>

                                        {/* Features List */}
                                        {(isMobile ? isExpanded : true) && (
                                            <div className="plan-features d-flex flex-column gap-1">
                                                {plan.features?.map((feature, featureIndex) => (
                                                    <div key={featureIndex} className={`plan-feature ${plan.popular ? "popular" : ""} d-flex align-items-start gap-2`}>
                                                        {<span
                                                            className={`icon ${feature.disabled ? "not-included" : ""} text-primary flex-shrink-0 mb-1 mt-1`}
                                                            size={20}
                                                            style={{
                                                                color: "#0046ff",
                                                                minWidth: '20px',
                                                                minHeight: '20px',
                                                                // Hidden by the feature's own flag, set in admin. This
                                                                // used to key off position — first row of every card but
                                                                // the first — which guessed at which lines were really
                                                                // list introductions rather than features.
                                                                display: feature.hide_icon ? "none" : "inline"
                                                            }}
                                                        />}
                                                        <span
                                                            className={`description ${feature.highlight ? "text-primary fw-semibold" : ""} ${feature.disabled ? "" : ""} mt-1`}
                                                            style={{
                                                                ...(feature.highlight ? {
                                                                    background: "linear-gradient(135deg, #0046ff, #cc5adf)",
                                                                    WebkitBackgroundClip: "text",
                                                                    WebkitTextFillColor: "transparent",
                                                                    backgroundClip: "text"
                                                                } : {})
                                                            }}
                                                        >
                                                            {feature?.text} {feature.disabled ? "(not included)" : ""}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </Box>
                                    {isMobile && <><Divider sx={{
                                        marginTop: "0.5rem",
                                        opacity: 1
                                    }} />

                                        <Box onClick={() => togglePlanFeatures(planKey)}
                                            sx={{
                                                padding: { xs: "1rem 0rem 0rem !important", sm: "1rem", md: "1rem" },
                                                textAlign: "center",
                                                display: "flex",
                                                gap: "1rem",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            <span>See Features</span>
                                            {isExpanded ? <Minus size={20} /> : <Plus size={20} />}
                                        </Box></>}
                                </div>
                            </Box>
                        );
                    })}
                </Box>
                <Grid sx={{
                    width: "100%",
                    display: "grid",
                    marginTop: "1.5rem"
                }}>
                    <IconButton
                        sx={{
                            borderRadius: "20px",
                            marginInline: "auto",
                            border: "2px solid transparent",
                            backgroundImage: `linear-gradient(#fff, #fff), linear-gradient(135deg, #7852f8 0%, #C45DFF 100%)`,
                            backgroundOrigin: "border-box",
                            backgroundClip: "padding-box, border-box",
                        }}
                        onClick={() => {
                            window.open(navigateToDomain("main", "pricing#compare-all-features"));
                        }}
                    >
                        <span
                            style={{
                                background: `linear-gradient(135deg, #7852f8 0%, #C45DFF 100%)`,
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                backgroundClip: "text",
                                fontWeight: 500,
                                fontSize: "1rem",
                                padding: "0.3rem"
                            }}
                        >
                            Compare All Features
                        </span>
                    </IconButton>
                </Grid>
            </section>

            {/* Payment Success Popup */}
            <PaymentSuccessPopup
                open={showPaymentStatus}
                onClose={() => { !isVerifying && setShowPaymentStatus(false) }}
                success={isPaymentSuccess}
                isLoading={isVerifying}
                title={isVerifying ? "Verifying Payment" : "Payment Successful"}
                subtitle={isVerifying ? "It may take few minutes. Do not refresh the page." : "Your payment has been processed and subscription plan has been upgraded."}
                Actions={() => {
                    return isVerifying ? "" : (
                        <Box container gap={2} display="flex" justifyContent="center">
                            <Primary
                                variant="contained"
                                sx={{ px: 3, minWidth: "130.7px !important" }}
                                onClick={() => {
                                    setShowPaymentStatus(false);
                                    trackEvent("Manage Subscription - Payment Success", {
                                        startup_name: startupDetails?.startup_name,
                                    });
                                    navigate("/manage_subscription", { replace: true });
                                    window.location.href = window.location.href;
                                }}
                            >
                                Continue
                            </Primary>
                        </Box>
                    );
                }}
            />

            {/* Promo Code Popup */}
            <EnterPromoCodePopup
                open={openPromoCodePopup}
                selectedPlan={selectedPlan}
                handleDirectPayment={handlePayment}
                handleClose={handleClosePromoCodePopup}
                handleSubmission={handleSubmitPromoCode}
                isSubmitting={isCreatingSubscription}
                isVerifyingCode={isSubmittingPromoCode}
            />

            {/* Billing Profile Popup */}
            <BillingProfilePopup
                open={openBillingProfilePopup}
                handleClose={() => setOpenBillingProfilePopup(false)}
                handleBillingSubmission={handleSubmitBillingProfile}
                isSubmitting={false}
                handleDirectPayment={() => {
                    setOpenBillingProfilePopup(false);
                }}
                selectedPlan={selectedPlan}
            // handleDirectPayment={handlePayment}
            />
        </>
    );
};

const PricingPage = () => {
    const { all_plans, loading_plans } = useSelector(state => state?.startupDetails);
    return (
        <>
            <PricingCards allPlans={all_plans} isLoadingPlans={loading_plans} key={"Pricing"} isPurchasable={true} />
        </>
    )
}

export default PricingPage
